import styled from '@emotion/styled';
import {
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
  status,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { m } from '../../motion';

/* The cropper is raised from a gallery that itself may sit inside the building
 * modal, so it takes `modalTop` for the same reason ConfirmDialog does: at
 * `modal` it painted underneath the surface that opened it. */
export const Scrim = m.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modalTop};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${spacing.md};
  background: ${palette.overlay};

  ${down('sm')} {
    padding: 0;
    align-items: stretch;
  }
`;

/* Bounded by the SCRIM, not by the viewport. The scrim above is
 * position: fixed / inset: 0, so on iOS it is already sized to the small
 * viewport — the height with the browser chrome showing — and its content box
 * has the padding subtracted for us. `100%` of it is therefore both simpler and
 * more correct than the `calc(100vh - 40px)` this used to carry: `vh` on iOS is
 * the LARGE viewport, so the card ran taller than the screen and the zoom
 * slider and Save button along its bottom edge sat behind Safari's toolbar. */
export const Card = m.div`
  width: min(720px, 100%);
  max-height: 100%;
  display: flex;
  flex-direction: column;
  background: ${palette.surface};
  border-radius: ${radius.lg};
  box-shadow: ${shadow.xl};
  overflow: hidden;

  ${down('sm')} {
    width: 100%;
    border-radius: 0;
  }
`;

export const Header = styled.header`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.lg} ${spacing.lg} ${spacing.md};
`;

export const Title = styled.h2`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.lg}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

/* "Photo 2 of 5" — the reason this is not decoration: a batch upload crops one
 * file at a time, and without a counter the modal reopening looks like the
 * save failed. */
export const Counter = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  color: ${palette.slate};
  white-space: nowrap;
`;

/* The crop surface. Dark, because a photo being cropped reads better against
 * ink than against white, and the 1:1 frame needs to be unmistakable. */
export const CropStage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  max-height: 52vh;
  max-height: 52dvh;
  background: ${palette.ink};
  overflow: hidden;
  touch-action: none;
`;

export const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  padding: ${spacing.lg};
`;

export const ControlRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
`;

export const ControlLabel = styled.label`
  min-width: 56px;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

/* A native range input, restyled. rsuite's slider was not used here on purpose:
 * this control has to respond to a pinch-zoom gesture forwarded from the crop
 * stage, and a native input is the one that stays in sync without a controlled
 * -component fight. */
export const Slider = styled.input`
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: ${radius.pill};
  background: ${palette.lineStrong};
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 20px;
    height: 20px;
    border-radius: ${radius.circle};
    background: ${palette.teal};
    border: 2px solid ${palette.surface};
    box-shadow: ${shadow.sm};
    transition: ${transition.transform};
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.12);
  }

  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border: 2px solid ${palette.surface};
    border-radius: ${radius.circle};
    background: ${palette.teal};
    box-shadow: ${shadow.sm};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

export const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  /* The token, not 40px. This modal is used on a phone to crop a photo just
     taken on that phone, so its rotate/reset controls are thumb targets and the
     44px minimum applies (design_system rule 5). */
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.medium};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
    background: ${palette.tealTintSoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const Hint = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slateLight};
`;

export const Footer = styled.footer`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg} ${spacing.lg};
  border-top: 1px solid ${palette.line};

  ${down('sm')} {
    flex-direction: column-reverse;

    & > button {
      width: 100%;
    }
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${status.alert.fg};
`;
